import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, HelpCircle } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQPage = () => {
  const faqs = [
    {
      category: 'Process',
      questions: [
        {
          question: 'How fast can I get an offer?',
          answer: 'We typically provide a cash offer within 24 hours of receiving your property information. In some cases, we can make an offer the same day. Our team works quickly to evaluate your property and present you with a fair market offer.',
        },
        {
          question: 'How does the selling process work?',
          answer: 'It\'s simple: First, you submit basic information about your property through our form or by calling us. Next, we evaluate your home and present a cash offer within 24 hours. If you accept, we handle all the paperwork and close on your timeline – often in as little as 7 days.',
        },
        {
          question: 'How long does it take to close?',
          answer: 'We can close in as little as 7 days, but we\'re flexible. If you need more time to move or arrange your affairs, we can schedule the closing for whenever works best for you – whether that\'s 7 days or 60 days out.',
        },
        {
          question: 'What documents are needed to sell?',
          answer: 'We handle most of the paperwork for you. Generally, you\'ll need proof of ownership (like a deed), a valid ID, and any existing mortgage information. Our team will guide you through exactly what\'s needed and help obtain any documents you don\'t have.',
        },
      ],
    },
    {
      category: 'Pricing & Fees',
      questions: [
        {
          question: 'Do you charge any fees or commissions?',
          answer: 'No, we don\'t charge any fees or commissions. Unlike traditional real estate transactions where you might pay 5-6% in agent commissions plus closing costs, we cover all costs ourselves. The offer we make is exactly what you receive at closing.',
        },
        {
          question: 'How do you determine your offer price?',
          answer: 'We evaluate several factors including your property\'s location, condition, size, recent comparable sales in the area, and current market conditions. Our goal is to provide a fair cash offer that reflects the true market value of your home.',
        },
        {
          question: 'Is the cash offer negotiable?',
          answer: 'Yes, we\'re always open to discussion. If you have questions about how we arrived at our offer or if your circumstances have changed, we\'re happy to revisit the numbers. Our goal is a fair deal for everyone.',
        },
      ],
    },
    {
      category: 'Property Conditions',
      questions: [
        {
          question: 'Do I need to make repairs before selling?',
          answer: 'Absolutely not! We buy houses in any condition – whether they need minor cosmetic updates or major structural repairs. You don\'t need to spend money fixing anything. We\'ll purchase your home as-is.',
        },
        {
          question: 'Do I need to clean the house before selling?',
          answer: 'No, you don\'t need to clean or remove any unwanted items. We buy homes as-is, including any furniture, belongings, or debris you want to leave behind. Just take what you want and leave the rest to us.',
        },
        {
          question: 'What types of properties do you buy?',
          answer: 'We buy single-family homes, condos, townhouses, multi-family properties, and even vacant land in some cases. Whether your property is in perfect condition or needs significant work, we\'re interested in making an offer.',
        },
      ],
    },
    {
      category: 'Obligations & Guarantees',
      questions: [
        {
          question: 'Am I obligated to accept the offer?',
          answer: 'Not at all. Our cash offers come with zero obligation. You\'re free to consider our offer, take time to think about it, or decline it entirely. There\'s no pressure and no commitment until you decide to move forward.',
        },
        {
          question: 'What if I change my mind after accepting?',
          answer: 'We understand that circumstances can change. While we prefer to complete agreed-upon deals, we\'re reasonable people. If something significant changes in your situation before closing, let us know and we\'ll work with you.',
        },
        {
          question: 'Is my information kept confidential?',
          answer: 'Yes, absolutely. All information you provide is kept strictly confidential and is only used to evaluate your property and prepare your offer. We never share your personal information with third parties.',
        },
      ],
    },
  ];

  return (
    <div data-testid="faq-page" className="pt-20">
      {/* Hero Section with Gradient */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white via-emerald/5 to-white relative overflow-hidden">
        {/* Floating decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div 
            className="absolute top-20 right-20 w-72 h-72 bg-emerald/10 rounded-full blur-3xl"
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-72 h-72 bg-gold/10 rounded-full blur-3xl"
            animate={{ y: [0, 20, 0], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <motion.div 
              className="w-20 h-20 bg-emerald/10 rounded-full flex items-center justify-center mx-auto mb-8"
              whileHover={{ scale: 1.1 }}
              animate={{ 
                boxShadow: ['0 0 0 0 rgba(4, 99, 7, 0.2)', '0 0 0 15px rgba(4, 99, 7, 0)', '0 0 0 0 rgba(4, 99, 7, 0)'],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <HelpCircle className="w-10 h-10 text-emerald" />
            </motion.div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-darkgreen mb-6">
              Frequently Asked Questions
            </h1>
            <p className="font-subheading text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions about selling your home to Green Palm Consulting? 
              Find answers to our most commonly asked questions below.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ Section with Gradient Background */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white via-gray-50/50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {faqs.map((category, categoryIndex) => (
            <AnimatedSection key={categoryIndex} delay={categoryIndex * 0.1} className="mb-12 last:mb-0">
              <motion.h2 
                data-testid={`faq-category-${categoryIndex}`}
                className="font-heading text-2xl font-bold text-darkgreen mb-6 pb-4 border-b border-gray-200"
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: -20 }}
                viewport={{ once: true }}
              >
                {category.category}
              </motion.h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                  >
                    <AccordionItem 
                      value={`${categoryIndex}-${index}`}
                      data-testid={`faq-item-${categoryIndex}-${index}`}
                      className="bg-white rounded-xl px-6 border border-gray-100 hover:border-emerald/30 hover:shadow-lg transition-all duration-300"
                    >
                      <AccordionTrigger className="text-left font-subheading font-semibold text-darkgreen hover:text-emerald py-5 [&[data-state=open]>svg]:text-emerald [&[data-state=open]]:text-emerald">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-gray-600 pb-5 leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 via-emerald/5 to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-1/2 left-1/2 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-darkgreen mb-6">
              Still Have Questions?
            </h2>
            <p className="font-subheading text-base md:text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              Our team is here to help. Reach out to us directly and we'll be happy 
              to answer any questions about our process or your specific situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/get-offer"
                data-testid="faq-get-offer-button"
                className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-base px-8 py-4 rounded-full group"
              >
                Get Your Offer
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <motion.a
                href="tel:+13072982879"
                data-testid="faq-call-button"
                className="inline-flex items-center justify-center bg-white border-2 border-darkgreen text-darkgreen hover:bg-darkgreen hover:text-white font-bold text-base px-8 py-4 rounded-full transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Call +1 307-298-2879
              </motion.a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
